import type { CSSProperties } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

type GroupsGraphProps = {
  data: Array<{
    request_manager: string;
    status: string;
    arrival_year: number;
    arrival_month: number;
  }>;
  selectedYear: number;
};

const axisTick = { fill: '#64748b', fontSize: 11 };
const axisLine = { stroke: '#e2e8f0' };
const gridStroke = '#cbd5e1';

const tooltipContentStyle: CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid rgb(226 232 240)',
  borderRadius: '0.5rem',
  boxShadow: '0 4px 6px -1px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.06)',
  fontSize: '12px',
  padding: '10px 12px',
};

const tooltipLabelStyle: CSSProperties = {
  color: '#475569',
  fontWeight: 600,
  marginBottom: '4px',
};

const legendWrapperStyle: CSSProperties = {
  paddingTop: '12px',
  fontSize: '12px',
};

export function GroupsGraph({ data, selectedYear }: GroupsGraphProps) {
  const groupsByMonth = data
    .filter(row => row.arrival_year === selectedYear)
    .reduce((acc, row) => {
      const month = row.arrival_month;
      if (!month) return acc;

      if (!acc[month]) {
        acc[month] = { created: 0, cancelled: 0 };
      }

      if (row.status === 'Booked') {
        acc[month].created += 1;
      } else if (row.status === 'Lost') {
        acc[month].cancelled += 1;
      }

      return acc;
    }, {} as Record<number, { created: number; cancelled: number }>);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const chartData = months.map((month, index) => {
    const monthNumber = index + 1;
    const monthData = groupsByMonth[monthNumber] || { created: 0, cancelled: 0 };

    return {
      month,
      Confirmed: monthData.created,
      Cancelled: monthData.cancelled,
    };
  });

  const heading = `Groups Confirmed vs Cancelled by Arrival Month (${selectedYear})`;

  return (
    <section
      className="mb-6 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
      aria-label={heading}
    >
      <div className="border-b border-slate-200/80 bg-slate-50/80 px-5 py-3.5">
        <h3 className="text-sm font-semibold tracking-tight text-slate-800">{heading}</h3>
      </div>
      <div className="px-3 pb-5 pt-4 sm:px-5">
        <div className="h-[300px] sm:h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }} barCategoryGap="12%">
              <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" horizontal vertical />
              <XAxis
                dataKey="month"
                tick={axisTick}
                axisLine={axisLine}
                tickLine={{ stroke: '#e2e8f0' }}
                dy={4}
              />
              <YAxis
                width={56}
                tick={axisTick}
                axisLine={axisLine}
                tickLine={{ stroke: '#e2e8f0' }}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value: number, name: string) => [value, name]}
                labelFormatter={String}
                labelStyle={tooltipLabelStyle}
                contentStyle={tooltipContentStyle}
                cursor={{ fill: 'rgb(24 141 130 / 0.06)' }}
              />
              <Legend wrapperStyle={legendWrapperStyle} iconType="square" iconSize={10} />
              <Bar dataKey="Cancelled" stackId="groups" fill="#ef4444" name="Cancelled" radius={[0, 0, 0, 0]} maxBarSize={44} />
              <Bar dataKey="Confirmed" stackId="groups" fill="#188d82" name="Confirmed" radius={[4, 4, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
